"use client";

import clsx from "clsx";
import Link from "next/link";
import { useId, useState } from "react";
import { themeImage } from "@/lib/media";
import { useModals } from "../ModalProvider";
import {
  headClass,
  inputClass,
  labelClass,
  messageBodyClass,
  messageClass,
  messageHeadClass,
  rowClass,
  submitButtonClass,
  submitClass,
} from "./authFormClasses";
import { fieldValue, useAuthForm } from "./useAuthForm";

/**
 * The account types, the colour of each one's button (hovering darkens it by
 * 5%, as the theme's darken() did), and where the ones with their own sign-up
 * journey send you.
 */
const ACCOUNT_TYPES = [
  {
    value: "member",
    label: "Sign up for a Personal account",
    colour: "bg-secondary hover:bg-[#015fcb]",
  },
  {
    value: "club",
    label: "Sign up for a Club account",
    colour: "bg-primary hover:bg-[#6d97ad]",
    redirect: "/register-your-club",
  },
  {
    value: "hss",
    label: "Sign up for an Association account",
    colour: "bg-alternative hover:bg-[#a1d6f6]",
    redirect: "/register-your-association/",
  },
];

const termsLinkClass = "text-[#32ade6] no-underline hover:underline";

/** Greyed out and out of reach while the form is sending, or on its way to another journey. */
const busyClass =
  "group-data-[loading=true]/form:pointer-events-none group-data-[loading=true]/form:opacity-50";

const DETAILS = [
  {
    name: "first_name",
    id: "register-form-first-name",
    label: "First name",
    type: "text",
    autoComplete: "given-name",
  },
  {
    name: "last_name",
    id: "register-form-last-name",
    label: "Last name",
    type: "text",
    autoComplete: "family-name",
  },
  {
    name: "email",
    id: "register-form-email",
    label: "Email",
    type: "email",
    autoComplete: "email",
  },
  {
    name: "password",
    id: "register-form-password",
    label: "Password",
    type: "password",
    autoComplete: "new-password",
  },
  {
    name: "confirm_password",
    id: "register-form-confirm-password",
    label: "Confirm password",
    type: "password",
    autoComplete: "new-password",
  },
];

/**
 * The port of fragments/auth/register-form.php and register-form.js.
 *
 * The form opens on a choice of account. Choosing one that has its own sign-up
 * journey goes there (a full page load, as the theme did); choosing a personal
 * account sets data-active, which swaps the choice for the details fields.
 *
 * On WordPress this posted to poppy_ajax_register, which checked the fields
 * (password at least six characters with a letter and a number, and matching
 * its confirmation), refused an email that already had a WordPress account,
 * and registered the member with the DAS app's API, which emailed an
 * activation link. Here it goes through submitForm("register"), and any field
 * errors it sends back go on their fields.
 *
 * The labels pointed at an id nothing had ("register-form"); they point at
 * their inputs here.
 */
export default function RegisterForm() {
  const { openModal } = useModals();
  const id = useId();
  const [active, setActive] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const { loading, message, success, errors, onSubmit, stateAttributes } =
    useAuthForm("register", (form) => ({
      account_type: fieldValue(form, "account-type"),
      first_name: fieldValue(form, "first_name"),
      last_name: fieldValue(form, "last_name"),
      email: fieldValue(form, "email"),
      password: fieldValue(form, "password"),
      confirm_password: fieldValue(form, "confirm_password"),
    }));

  /*
   * The switch to signing in closes both halves of the form. Under the choice
   * of account it is set off by a short rule above it.
   */
  const signinSwitch = (underChoice: boolean) => (
    <div
      className={clsx(
        "text-center text-[13px] font-light tracking-[-0.6px] text-[#636366]",
        underChoice &&
          "relative mt-5 pt-5 before:absolute before:top-0 before:left-1/2 before:h-[2px] before:w-[60%] before:-translate-x-1/2 before:bg-[#016e51] before:content-['']",
      )}
    >
      <p>
        Already have an account?{" "}
        <a
          href="#"
          className="js-trigger-modal"
          onClick={(event) => {
            event.preventDefault();
            openModal("signin-modal");
          }}
        >
          Sign in
        </a>
      </p>
    </div>
  );

  return (
    <form
      action=""
      className="group/form js-register-form"
      id={`register-form-${id}`}
      onSubmit={onSubmit}
      {...stateAttributes}
      data-loading={loading || redirecting}
      data-active={active}
    >
      <div className={headClass}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={themeImage("logo-dark.png")} alt="DAS Academies" />
      </div>

      <div className={messageClass}>
        <h3 className={`${messageHeadClass} js-form-message-head`}>
          {message?.header || ""}
        </h3>

        <p className={`${messageBodyClass} js-form-message-body`}>
          {message?.body || ""}
        </p>
      </div>

      {/* On success the form goes and only the message stays. */}
      {!success && (
        <div className="mb-7.5 js-form-body">
          <div
            className={clsx(busyClass, "group-data-[active=true]/form:hidden")}
          >
            <div className="mb-7.5 w-full">
              <ul className="m-0 list-none p-0">
                {ACCOUNT_TYPES.map((type) => (
                  <li key={type.value} className="m-0 p-0 [&+li]:mt-7.5">
                    <label
                      className={clsx(
                        "block w-full cursor-pointer rounded-[12px] px-5 py-[15px] text-center text-[14px] font-bold text-white",
                        type.colour,
                      )}
                    >
                      <input
                        type="radio"
                        name="account-type"
                        value={type.value}
                        className="absolute opacity-0 js-account-type"
                        data-redirect={type.redirect}
                        onChange={() => {
                          if (type.redirect) {
                            setRedirecting(true);
                            window.location.assign(type.redirect);

                            return;
                          }

                          setActive(true);
                        }}
                      />
                      {type.label}
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {signinSwitch(true)}
          </div>

          <div className="hidden group-data-[active=true]/form:block">
            <div className={clsx("mb-7.5", busyClass)}>
              {DETAILS.map((detail) => (
                <div key={detail.name} className={rowClass}>
                  <div
                    className="group/field js-field"
                    data-error={!!errors[detail.name]}
                  >
                    <label htmlFor={detail.id} className={labelClass}>
                      {detail.label}
                    </label>

                    <input
                      type={detail.type}
                      id={detail.id}
                      name={detail.name}
                      className={inputClass}
                      placeholder={detail.label}
                      autoComplete={detail.autoComplete}
                      required
                    />

                    <span className="mt-1.5 hidden text-[12px] text-danger group-data-[error=true]/field:block js-field-error">
                      {errors[detail.name] || ""}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className={`${submitClass} js-form-submit`}>
              <button type="submit" className={submitButtonClass}>
                <span className="group-data-[loading=true]/form:hidden">
                  Continue
                </span>

                {/* The theme's white spinner icon, in place of the label while sending. */}
                <i className="relative hidden size-5 bg-[url('/theme/images/spinner-white.svg')] bg-cover bg-center bg-no-repeat align-top indent-[-4004px] text-[0px] leading-[0] group-data-[loading=true]/form:inline-block"></i>
              </button>
            </div>

            <div className="mx-0 mt-2.5 mb-7.5 text-center text-[14px] font-light tracking-[-0.6px] text-[#636366]">
              <p>
                By continuing you agree to AFVBC{" "}
                <Link className={termsLinkClass} href="/terms-and-conditions">
                  Terms of Service
                </Link>{" "}
                and acknowledge you’ve read our{" "}
                <Link className={termsLinkClass} href="/privacy-policy">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>

            {signinSwitch(false)}
          </div>
        </div>
      )}
    </form>
  );
}
